import { useGetFileSubmissionRecipientGroups } from '@/app/roleHooks';
import FileSubmissionBtn from '@/components/MantineFileManager/btn/FileSubmissionBtn';
import type { RequestQuery } from '@/features/api/directus';
import {
  useGetFileSubmissionCountQuery,
  useGetFileSubmissionsQuery,
  useUpdateFileSubmissionMutation,
} from '@/features/api/endpoints/fileSubmissionEndpoints';
import { useGetDirectusUsersQuery } from '@/features/api/endpoints/userEndpoints';
import { fileSubmissionStatus, type FileSubmissionStatus } from '@/models/fileSubmission.type';
import type { FileSubmission } from '@/models/types';
import { useGetCurrentUser, useIsAdmin } from '@/shared/auth/useDirectusUser';
import { DATE_TIME_FORMAT } from '@/shared/utils/date-utils';
import { getFileName } from '@/shared/utils/file.utils';
import { getUserLabel } from '@/shared/utils/user.utils';
import { Anchor, Button, Center, Group, Loader, Select, Stack, Text } from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { useDocumentTitle } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { skipToken } from '@reduxjs/toolkit/query';
import { useTranslate } from '@tolgee/react';
import dayjs from 'dayjs';
import isObject from 'lodash/isObject';
import { DataTable, type DataTableColumn } from 'mantine-datatable';
import { useState } from 'react';

const isFileSubmissionStatus = (value: string): value is FileSubmissionStatus =>
  fileSubmissionStatus.includes(value as FileSubmissionStatus);

const RECORDS_PER_PAGE = 25;

const UploadPage = () => {
  const { t } = useTranslate();
  const { status: userStatus, user } = useGetCurrentUser();
  const isAdmin = useIsAdmin();
  const [page, setPage] = useState(1);
  const [userFilter, setUserFilter] = useState<string | null>(null);
  const [dateFrom, setDateFrom] = useState<string | null>(null);
  const [dateTo, setDateTo] = useState<string | null>(null);
  const [recipientGroupFilter, setRecipientGroupFilter] = useState<number | null>(null);
  const [statusFilter, setStatusFilter] = useState<FileSubmissionStatus | null>(null);
  const [updatingSubmissionId, setUpdatingSubmissionId] = useState<number | null>(null);
  const [updateFileSubmission] = useUpdateFileSubmissionMutation();

  const { data: users = [] } = useGetDirectusUsersQuery(
    isAdmin
      ? {
          fields: ['id', 'first_name', 'last_name', 'email'],
          sort: ['last_name', 'first_name'],
        }
      : undefined,
  );

  const { data: recipientGroups = [] } = useGetFileSubmissionRecipientGroups();

  const submissionFilter: NonNullable<RequestQuery<FileSubmission>['filter']> = {
    ...(isAdmin || !user ? {} : { user_created: { _eq: user.id } }),
    ...(isAdmin && userFilter ? { user_created: { _eq: userFilter } } : {}),
    ...(recipientGroupFilter ? { recipientgroup: { _eq: recipientGroupFilter } } : {}),
    ...(statusFilter ? { status: { _eq: statusFilter } } : {}),
    ...(dateFrom
      ? {
          date_created: {
            _gte: dayjs(dateFrom).startOf('day').toISOString(),
          } as never,
        }
      : {}),
    ...(dateTo
      ? {
          date_created: {
            ...(dateFrom ? { _gte: dayjs(dateFrom).startOf('day').toISOString() } : {}),
            _lt: dayjs(dateTo).add(1, 'day').startOf('day').toISOString(),
          } as never,
        }
      : {}),
  };

  const submissionQuery: RequestQuery<FileSubmission> = {
    fields: [
      '*',
      ...(isAdmin ? [{ user_created: ['id', 'first_name', 'last_name', 'email'] as const }] : []),
      {
        files: [
          '*',
          {
            directus_files_id: ['id', 'filename_download', 'title'],
          },
        ],
        role: ['id', 'name'],
      },
    ],
    filter: submissionFilter,
    sort: ['-date_created'],
    limit: RECORDS_PER_PAGE,
    offset: (page - 1) * RECORDS_PER_PAGE,
  };

  const {
    data: submissions = [],
    isError,
    isFetching,
  } = useGetFileSubmissionsQuery(user ? submissionQuery : skipToken);
  const { data: totalRecords = 0 } = useGetFileSubmissionCountQuery(
    user ? { filter: submissionFilter } : skipToken,
  );

  const userOptions = users.map(currentUser => ({
    value: currentUser.id,
    label: getUserLabel(currentUser, t('upload.unavailable')),
  }));

  const resetFilters = (close?: () => void) => {
    setUserFilter(null);
    setDateFrom(null);
    setDateTo(null);
    setRecipientGroupFilter(null);
    setStatusFilter(null);
    setPage(1);
    close?.();
  };

  const handleStatusChange = async (submission: FileSubmission, value: string | null) => {
    if (!value || value === submission.status || !isFileSubmissionStatus(value)) {
      return;
    }

    setUpdatingSubmissionId(submission.id);
    try {
      await updateFileSubmission({ id: submission.id, body: { status: value } }).unwrap();
      notifications.show({
        color: 'green',
        message: t('upload.update.success'),
      });
    } catch {
      notifications.show({
        color: 'red',
        message: t('upload.update.error'),
      });
    } finally {
      setUpdatingSubmissionId(null);
    }
  };

  useDocumentTitle(t('upload.title'));

  return (
    <Stack gap="md">
      <Group justify="space-between" align="center">
        <FileSubmissionBtn />
      </Group>

      {isError || userStatus === 'error' ? (
        <Text c="red">{t('upload.error')}</Text>
      ) : userStatus === 'loading' || isFetching ? (
        <Center h={120}>
          <Loader size="sm" />
        </Center>
      ) : (
        <DataTable
          withTableBorder
          withColumnBorders
          fetching={isFetching}
          records={submissions}
          idAccessor="id"
          noRecordsText={t('upload.empty')}
          columns={
            [
              ...(isAdmin
                ? ([
                    {
                      accessor: 'user_created',
                      title: t('upload.columns.user_created'),
                      textAlign: 'center',
                      width: 160,
                      filter: ({ close }) => (
                        <Stack gap="xs">
                          <Select
                            clearable
                            searchable
                            label={t('upload.filters.user')}
                            data={userOptions}
                            value={userFilter}
                            onChange={value => {
                              setUserFilter(value);
                              setPage(1);
                            }}
                          />
                          <Button variant="subtle" onClick={() => resetFilters(close)}>
                            {t('upload.filters.reset')}
                          </Button>
                        </Stack>
                      ),
                      filtering: Boolean(userFilter),
                      render: ({ user_created }: FileSubmission) =>
                        getUserLabel(user_created, t('upload.unavailable')),
                    },
                  ] satisfies DataTableColumn<FileSubmission>[])
                : []),
              {
                accessor: 'date_created',
                title: t('upload.columns.date_created'),
                textAlign: 'center',
                width: 150,
                filter: ({ close }) => (
                  <Stack gap="xs">
                    <DateInput
                      clearable
                      label={t('upload.filters.from')}
                      value={dateFrom}
                      onChange={value => {
                        setDateFrom(value);
                        setPage(1);
                      }}
                    />
                    <DateInput
                      clearable
                      label={t('upload.filters.to')}
                      value={dateTo}
                      onChange={value => {
                        setDateTo(value);
                        setPage(1);
                      }}
                    />
                    <Button variant="subtle" onClick={() => resetFilters(close)}>
                      {t('upload.filters.reset')}
                    </Button>
                  </Stack>
                ),
                filtering: Boolean(dateFrom || dateTo),
                render: ({ date_created }: FileSubmission) =>
                  date_created
                    ? dayjs(date_created).format(DATE_TIME_FORMAT)
                    : t('upload.unavailable'),
              },
              {
                accessor: 'recipientgroup',
                title: t('upload.columns.recipientgroup'),
                filter: ({ close }) => (
                  <Stack gap="xs">
                    <Select
                      clearable
                      label={t('upload.filters.recipient')}
                      data={recipientGroups.map(role => ({
                        value: role.id,
                        label: role.name as string,
                      }))}
                      value={recipientGroupFilter}
                      onChange={value => {
                        setRecipientGroupFilter(value);
                        setPage(1);
                      }}
                    />
                    <Button variant="subtle" onClick={() => resetFilters(close)}>
                      {t('upload.filters.reset')}
                    </Button>
                  </Stack>
                ),
                filtering: Boolean(recipientGroupFilter),
                render: ({ role }: FileSubmission) => {
                  if (isObject(role)) {
                    return role?.name || t('upload.unavailable');
                  }
                  return role;
                },
              },
              {
                accessor: 'status',
                title: t('upload.columns.status'),
                filter: ({ close }) => (
                  <Stack gap="xs">
                    <Select
                      clearable
                      label={t('upload.filters.status')}
                      data={fileSubmissionStatus.map(status => ({
                        value: status,
                        label: t(`upload.status.${status}`),
                      }))}
                      value={statusFilter}
                      onChange={value => {
                        setStatusFilter(value && isFileSubmissionStatus(value) ? value : null);
                        setPage(1);
                      }}
                    />
                    <Button variant="subtle" onClick={() => resetFilters(close)}>
                      {t('upload.filters.reset')}
                    </Button>
                  </Stack>
                ),
                filtering: Boolean(statusFilter),
                render: submission =>
                  isAdmin ? (
                    <Select
                      aria-label={t('upload.actions.change_status')}
                      data={fileSubmissionStatus.map(status => ({
                        value: status,
                        label: t(`upload.status.${status}`),
                      }))}
                      disabled={updatingSubmissionId === submission.id}
                      value={submission.status}
                      onChange={value => handleStatusChange(submission, value)}
                      w={140}
                    />
                  ) : (
                    t(`upload.status.${submission.status}`)
                  ),
              },
              {
                accessor: 'comment',
                title: t('upload.columns.comment'),
                render: ({ comment }: FileSubmission) => comment || t('upload.unavailable'),
              },
              {
                accessor: 'files',
                title: t('upload.columns.files'),
                render: ({ files }: FileSubmission) =>
                  files.length > 0 ? (
                    <Stack gap={0}>
                      {files.map(file => {
                        const fileName = getFileName(file, t('upload.unavailable'));
                        const fileId =
                          typeof file.directus_files_id === 'string'
                            ? file.directus_files_id
                            : file.directus_files_id?.id;

                        return fileId ? (
                          <Anchor
                            key={file.id}
                            href={`${import.meta.env.VITE_DIRECTUS_URL}/assets/${fileId}?download`}
                            download={fileName}
                            size="sm"
                          >
                            {fileName}
                          </Anchor>
                        ) : (
                          <Text key={file.id} size="sm">
                            {fileName}
                          </Text>
                        );
                      })}
                    </Stack>
                  ) : (
                    t('upload.unavailable')
                  ),
              },
            ] satisfies DataTableColumn<FileSubmission>[]
          }
          page={page}
          onPageChange={setPage}
          recordsPerPage={RECORDS_PER_PAGE}
          totalRecords={totalRecords}
          paginationWithEdges
          paginationText={({ from, to, totalRecords: total }) =>
            t('upload.pagination', { from, to, total })
          }
        />
      )}
    </Stack>
  );
};

export default UploadPage;
